import { FunctionComponent, useState } from 'react';

// Libs
import classnames from 'classnames';
import { PageView } from '@Components/Utils/Lazy/Script';

// Contexts
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Types
import { NextSeoProps } from 'next-seo';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';

// Components
import Page from '@Components/Utils/Page';
import ImgLazy from '@Components/Utils/Lazy/Image';
import ContactForm from '@Pages/lien-he/Components/ContactForm';
import LayoutContainer from '@Components/Layout/Components/Container';
import Breadcrumb, { PathLevelProps } from '@Components/UI/Breadcrumb';
import DownloadApp from '@Pages/bai-viet/Components/DownloadApp';

// Styles
import styles from './Contact.module.scss';

interface ContactProps {}

const paths: Array<PathLevelProps> = [
    {
        label: 'Liên hệ',
    },
];

const ADDRESS = [
    {
        map: {
            key: 'hcm',
            lat_long: '10.804525952015734, 106.70929448274266',
        },
        info: (
            <>
                Trụ sở Thành phố Hồ Chí Minh
                <br />
                69 Đinh Bộ Lĩnh, Phường 24, Quận Bình Thạnh.
            </>
        ),
    },
    {
        map: {
            key: 'hn',
            lat_long: '20.99322982029903, 105.78692841930852',
        },
        info: (
            <>
                Chi nhánh Thành phố Hà Nội
                <br />
                Lầu 2, Toà A3 Ecolife, số 58 Tố Hữu, Nam Từ Liêm.
            </>
        ),
    },
    {
        map: {
            key: 'ct',
            lat_long: '10.03116179492538, 105.75248602658604',
        },
        info: (
            <>
                Văn Phòng Đại Diện Tại Cần Thơ
                <br />
                368B Nguyễn Văn Cừ nối dài, Phường An Khánh, Quận Ninh Kiều.
            </>
        ),
    },
];

const Contact: FunctionComponent<InferGetServerSidePropsType<typeof getServerSideProps> & ContactProps> = () => {
    const { deviceType } = useViewDetected();

    const isMobile = deviceType !== 'desktop';

    const [address, setAddress] = useState<{ key: string; lat_long: string }>({
        key: 'hcm',
        lat_long: '10.804525952015734, 106.70929448274266',
    });

    const renderAdress = () => {
        return ADDRESS.map((addressItem, index) => {
            const addressClassName = classnames([
                styles.address_item,
                { [styles.active]: addressItem.map.key === address.key },
            ]);

            return (
                <div
                    className={addressClassName}
                    key={`address-item-${index}`}
                    onClick={() => setAddress(addressItem.map)}
                >
                    {addressItem.info}
                </div>
            );
        });
    };

    const wrapperClassName = classnames([styles.wrapper, { [styles.mobile]: isMobile }]);
    const breadcrumbClassName = classnames([styles.breadcrumb, { [styles.breadcrumb_mobile]: isMobile }]);

    return (
        <Page className={styles.page}>
            <LayoutContainer className={styles.layout_container}>
                <div className={breadcrumbClassName}>
                    <Breadcrumb paths={paths} />
                </div>

                <div className={wrapperClassName}>
                    <div className={styles.form_wrapper}>
                        <div className={styles.form_title}>Liên hệ eDoctor</div>

                        <ContactForm />
                    </div>
                    <div className={styles.address_wrapper}>
                        <div className={styles.title}>Địa chỉ liên hệ</div>

                        <div className={styles.address}>
                            <div className={styles.map_wrapper}>
                                <a target="__blank" href={`https://maps.google.com/?q=${address.lat_long}`}>
                                    <ImgLazy
                                        width={347}
                                        height={260}
                                        src={`/assets/lien-he/${address.key}.png`}
                                        className={classnames(['animate__animated animate__fadeIn', styles.map])}
                                    />
                                </a>

                                <DownloadApp className={styles.download_app} />
                            </div>

                            <div className={styles.info}>
                                <div className={styles.contact_item}>
                                    <ImgLazy src="/assets/global/phone.png" width={24} height={24} />
                                    Hotline:&nbsp;
                                    <a href="tel:19006115">19006115</a>
                                </div>

                                <div className={styles.contact_item}>
                                    <ImgLazy src="/assets/global/letter.png" width={24} height={24} />
                                    Email:&nbsp;
                                    <a href="mailto:hotro@edoctor.vn">hotro@edoctor.vn</a>
                                </div>

                                <div className={styles.contact_item}>
                                    <ImgLazy src="/assets/global/address_book.png" width={24} height={24} />
                                    Địa chỉ liên hệ:
                                </div>

                                {renderAdress()}
                            </div>
                        </div>
                    </div>
                </div>
            </LayoutContainer>
            <PageView />
        </Page>
    );
};

Contact.defaultProps = {};

export default Contact;

export const getServerSideProps: GetServerSideProps = async () => {
    const title = 'eDoctor: Liên hệ';
    const seo: NextSeoProps = {
        title,
        openGraph: {
            title,
            type: 'website',
        },
    };

    return {
        props: {
            seo,
        },
    };
};
